import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { ArrowUpRight, ScanLine, FileText, Target } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import { ProgressTimeline } from "@/components/ProgressTimeline";
import { ScoreHistoryCard } from "@/components/ScoreHistoryCard";

const TYPE_LABELS = {
  collection: "Collections",
  late_payment: "Late Payments",
  charge_off: "Charge-offs",
  inquiry: "Hard Inquiries",
  bankruptcy: "Bankruptcies",
  repossession: "Repossessions",
  other: "Other",
};

const STATUS_LABELS = {
  draft: "Draft",
  sent: "Sent",
  pending: "Pending",
  resolved: "Resolved",
  rejected: "Rejected",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [tipsSnapshot, setTipsSnapshot] = useState(null);
  const [score, setScore] = useState("");
  const [target, setTarget] = useState("");
  const [savingScore, setSavingScore] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      setSummary(data);
      if (data.current_score) setScore(String(data.current_score));
      if (data.target_score) setTarget(String(data.target_score));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
    // Also load the tips snapshot for the onboarding wizard (cached 24h, fast path for new users)
    api
      .get("/tips")
      .then(({ data }) => setTipsSnapshot(data.user_snapshot || {}))
      .catch(() => {});
  }, [load]);

  const saveScore = async (e) => {
    e.preventDefault();
    setSavingScore(true);
    try {
      await api.post("/profile", {
        current_score: score ? parseInt(score, 10) : null,
        target_score: target ? parseInt(target, 10) : null,
      });
      toast.success("Score saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingScore(false);
    }
  };

  const chartData = (summary?.history || []).map((h) => ({
    date: new Date(h.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    score: h.score,
  }));

  return (
    <AppShell>
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline mb-3">Overview</div>
          <h1 className="font-display text-5xl md:text-6xl leading-none">
            Hey, {user?.name?.split(" ")[0] || "friend"}.
          </h1>
          <p className="text-[#A8ADA5] mt-3">Here's the current state of your credit game.</p>
        </div>
        <div className="text-right">
          <div className="overline mb-1">last sync</div>
          <div className="font-mono text-sm text-[#F5F3EE]">
            {new Date().toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      </div>

      {/* Onboarding wizard shows only if any of the 3 first-time steps aren't complete */}
      {tipsSnapshot && <OnboardingWizard snapshot={tipsSnapshot} />}

      {/* Score history graph — actual FICO vs projected lift */}
      <ScoreHistoryCard />

      {/* Score journey timeline */}
      <ProgressTimeline />

      {/* Top metrics */}
      <div className="grid md:grid-cols-4 gap-px bg-[#26282C] border border-[#26282C] mb-6 stagger" data-testid="dashboard-metrics">
        <div className="md:col-span-2 bg-[#0B0C0E] p-6 lg:p-8">
          <div className="flex items-start justify-between mb-4">
            <div className="overline">Credit score</div>
            <span className="text-[10px] font-mono text-[#7CFF9B]">● live</span>
          </div>
          <div className="flex items-end gap-4">
            <div className="ticker text-7xl text-[#D5FF3F]" data-testid="metric-current-score">
              {summary?.current_score || "—"}
            </div>
            <div className="text-sm text-[#6D716C] pb-3 font-mono">
              tgt · <span className="text-[#F5F3EE]">{summary?.target_score || "—"}</span>
            </div>
          </div>
          {chartData.length > 1 && (
            <div className="h-28 mt-6 -mx-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <XAxis dataKey="date" hide />
                  <YAxis hide domain={["dataMin - 20", "dataMax + 20"]} />
                  <Tooltip
                    contentStyle={{
                      background: "#131518",
                      border: "1px solid #26282C",
                      borderRadius: "4px",
                      fontFamily: "JetBrains Mono, monospace",
                      color: "#F5F3EE",
                      fontSize: "12px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="score"
                    stroke="#D5FF3F"
                    strokeWidth={2}
                    dot={{ fill: "#D5FF3F", r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
          <form onSubmit={saveScore} className="mt-6 flex gap-2 items-end">
            <div className="flex-1">
              <Label htmlFor="score" className="text-[10px] uppercase tracking-widest text-[#6D716C] font-mono">Current</Label>
              <Input id="score" type="number" value={score} onChange={(e) => setScore(e.target.value)} data-testid="score-current-input" className="mt-1 rounded-sm h-9 font-mono" placeholder="580" />
            </div>
            <div className="flex-1">
              <Label htmlFor="target" className="text-[10px] uppercase tracking-widest text-[#6D716C] font-mono">Target</Label>
              <Input id="target" type="number" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="score-target-input" className="mt-1 rounded-sm h-9 font-mono" placeholder="720" />
            </div>
            <Button type="submit" data-testid="score-save-btn" disabled={savingScore} className="h-9 rounded-sm btn-lime px-4">
              {savingScore ? "…" : "Save"}
            </Button>
          </form>
        </div>

        <div className="bg-[#0B0C0E] p-6 lg:p-8 lift">
          <div className="overline mb-4">Negative items</div>
          <div className="ticker text-6xl text-[#FF6B4A]" data-testid="metric-neg-items">
            {summary?.negative_items_count ?? 0}
          </div>
          <div className="text-sm text-[#6D716C] mt-2 font-mono">flagged</div>
          <Link to="/app/items" className="text-xs text-[#D5FF3F] uppercase tracking-widest font-mono mt-6 inline-flex items-center">
            Review <ArrowUpRight size={12} className="ml-1" />
          </Link>
        </div>

        <div className="bg-[#0B0C0E] p-6 lg:p-8 lift">
          <div className="overline mb-4">Active disputes</div>
          <div className="ticker text-6xl text-[#F5F3EE]" data-testid="metric-disputes">
            {summary?.disputes_count ?? 0}
          </div>
          <div className="text-sm text-[#6D716C] mt-2 font-mono">
            <span className="text-[#7CFF9B]">{summary?.by_status?.resolved || 0}</span> resolved
          </div>
          <Link to="/app/disputes" className="text-xs text-[#D5FF3F] uppercase tracking-widest font-mono mt-6 inline-flex items-center">
            Tracker <ArrowUpRight size={12} className="ml-1" />
          </Link>
        </div>
      </div>

      {/* Actions */}
      <div className="grid md:grid-cols-3 gap-px bg-[#26282C] border border-[#26282C] mb-6">
        <Link
          to="/app/scan"
          data-testid="quick-action-scan"
          className="group bg-[#D5FF3F] text-[#0B0C0E] p-8 lift block"
        >
          <ScanLine size={22} strokeWidth={1.75} className="mb-8" />
          <div className="font-display text-3xl leading-none">AI Scan</div>
          <p className="text-[#0B0C0E]/70 text-sm mt-3 max-w-xs">
            Upload your report. Claude reads it end-to-end and extracts every disputable mark.
          </p>
          <div className="text-xs mt-6 inline-flex items-center uppercase tracking-widest font-mono">
            Run scan <ArrowUpRight size={13} className="ml-1" />
          </div>
        </Link>
        <Link
          to="/app/items"
          data-testid="quick-action-items"
          className="group bg-[#0B0C0E] p-8 lift block"
        >
          <FileText size={22} strokeWidth={1.5} className="mb-8 text-[#F5F3EE]" />
          <div className="font-display text-3xl leading-none">Add manually</div>
          <p className="text-[#A8ADA5] text-sm mt-3 max-w-xs">
            Know a collection or late payment already? Log it and dispute in one click.
          </p>
          <div className="text-xs mt-6 inline-flex items-center text-[#D5FF3F] uppercase tracking-widest font-mono">
            Add item <ArrowUpRight size={13} className="ml-1" />
          </div>
        </Link>
        <Link
          to="/app/tips"
          data-testid="quick-action-tips"
          className="group bg-[#0B0C0E] p-8 lift block"
        >
          <Target size={22} strokeWidth={1.5} className="mb-8 text-[#7CFF9B]" />
          <div className="font-display text-3xl leading-none">Next moves</div>
          <p className="text-[#A8ADA5] text-sm mt-3 max-w-xs">
            Five things to do this week — ranked by expected score impact.
          </p>
          <div className="text-xs mt-6 inline-flex items-center text-[#D5FF3F] uppercase tracking-widest font-mono">
            Coach me <ArrowUpRight size={13} className="ml-1" />
          </div>
        </Link>
      </div>

      {/* Breakdowns */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card-surface p-6 lg:p-8">
          <div className="overline mb-6">Items by type</div>
          {summary && Object.keys(summary.by_type || {}).length ? (
            <div className="space-y-3">
              {Object.entries(summary.by_type).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-[#26282C] pb-3 last:border-0">
                  <span className="text-sm text-[#F5F3EE]">{TYPE_LABELS[k] || k}</span>
                  <span className="ticker text-sm text-[#D5FF3F]">{v}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#6D716C]">No negative items yet.</p>
          )}
        </div>
        <div className="card-surface p-6 lg:p-8">
          <div className="overline mb-6">Disputes by status</div>
          {summary && Object.values(summary.by_status || {}).some((v) => v > 0) ? (
            <div className="space-y-3">
              {Object.entries(summary.by_status).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between border-b border-[#26282C] pb-3 last:border-0">
                  <span className="text-sm text-[#F5F3EE]">{STATUS_LABELS[k] || k}</span>
                  <span className="ticker text-sm text-[#D5FF3F]">{v}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#6D716C]">No disputes yet.</p>
          )}
        </div>
      </div>
    </AppShell>
  );
}
